// src/Pages/Events/EventListPage.tsx
import React, { useEffect, useState } from "react";
import { Center, Loader } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billede1.jpg";
import EventCardV2 from "../../components/EventCardV2";
import { EventDto } from "../Admin/ID/types";
import { useLanguage } from "../../context/LanguageContext";
import { type EntryPoint } from "../../utils/authRedirect";

import ApiErrorDiagnostic from "../../Diagnostic/ApiErrorDiagnostic";
import {
    ApiDiagnosticInfo,
    createApiDiagnostic,
} from "../../Diagnostic/ApiDiagnostic";

type Props = {
    entry: EntryPoint // "volunteer" | "team" | "media" | "intern"
    title?: string;
    description?: string;
    internalFilter?: boolean | null;
};

const EventListPage: React.FC<Props> = ({
    entry,
    title,
    description,
    internalFilter = false,
}) => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [loading, setLoading] = useState(true);

    const [diagnostic, setDiagnostic] =
        useState<ApiDiagnosticInfo | null>(null);

    const navigate = useNavigate();
    const { setLanguage } = useLanguage();

    useEffect(() => {
        setLanguage("en");
    }, [setLanguage]);

    useEffect(() => {
        const loadEvents = async () => {
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiUrl = `${apiBaseUrl}/api/events`;

            const started = performance.now();

            setLoading(true);
            setDiagnostic(null);

            const controller = new AbortController();

            const timeoutId = window.setTimeout(() => {
                controller.abort();
            }, 20000);


            try {
                const response = await fetch(apiUrl, {
                    cache: "no-store",
                    signal: controller.signal,
                });

                // Vi har fået svar - timeout behøves ikke længere
                clearTimeout(timeoutId);

                if (!response.ok) {
                    const error: any = new Error(
                        `HTTP error! Status: ${response.status}`
                    );

                    error.status = response.status;
                    error.statusText = response.statusText;

                    throw error;
                }

                const data = await response.json();

                setEvents(data);
            } catch (error: any) {

                clearTimeout(timeoutId);

                console.error("Error fetching events:", error);

                const durationMs = Math.round(
                    performance.now() - started
                );

                const info = createApiDiagnostic(error, {
                    apiUrl,
                    durationMs,
                });

                console.error("API DIAGNOSTIC:", info);

                setDiagnostic(info);
            } finally {
                clearTimeout(timeoutId);
                setLoading(false);
            }
        };

        loadEvents();
    }, []);

    const filteredEvents = events.filter((event) => {
        if (
            internalFilter === null ||
            internalFilter === undefined
        ) {
            return true;
        }

        return event.isInternal === internalFilter;
    });

    const pageConfig = {
        team: {
            titleEn: "Team Page",
            titleDa: "Team Side",
            descriptionEn: "Team Management...",
            descriptionDa: "Team Administration...",
            badgeText: "TEAM",
        },

        media: {
            titleEn: "Media Page",
            titleDa: "Medie Side",
            descriptionEn: "Media Management...",
            descriptionDa: "Medie Administration...",
            badgeText: "MEDIA",
        },

        intern: {
            titleEn: "Internal Page",
            titleDa: "Intern Side",
            descriptionEn: "Internal Events...",
            descriptionDa: "Interne Events...",
            badgeText: "INTERN",
        },

        club: {
            titleEn: "Club Page",
            titleDa: "Klub Side",
            descriptionEn: "Club Management...",
            descriptionDa: "Klub Administration...",
            badgeText: "CLUB",
        },

        volunteer: {
            titleEn: "Volunteer Page",
            titleDa: "Frivillig Side",
            descriptionEn: "Volunteer Events...",
            descriptionDa: "Frivillige Events...",
            badgeText: "VOLUNTEER",
        },
    } as const;

    const config = pageConfig[entry] ?? pageConfig.team;

    function getTargetAfterLogin(
        entry: string,
        eventId: number
    ) {
        switch (entry) {
            case "team":
                return `/TeamDashboard/${eventId}`;

            case "media":
                return `/MediaDashboard/${eventId}`;

            case "club":
                return `/ClubDashboard/${eventId}`;

            case "intern":
                return `/InternDashboard/${eventId}`;

            case "volunteer":
                return `/VolunteerGroupsDashboard/${eventId}`;

            default:
                return `/EventPage/${eventId}`;
        }
    }

    const handleSelectEvent = (eventId: number) => {
        const isLoggedIn =
            !!sessionStorage.getItem("authToken");

        if (!isLoggedIn) {
            navigate(
                `/login?entry=${entry}&eventId=${eventId}`
            );
            return;
        }

        navigate(getTargetAfterLogin(entry, eventId));
    };

    return (
        <div className="role-page">
            <HeroSectionV2
                titleEn={config.titleEn}
                titleDa={config.titleDa}
                descriptionEn={config.descriptionEn}
                descriptionDa={config.descriptionDa}
                imageSrc={Billede1}
                badgeText={config.badgeText}
                logoSrc=""
            />

            <section className="events-section">
                {loading && (
                    <Center mt="md">
                        <Loader />
                    </Center>
                )}

                {!loading && diagnostic && (
                    <ApiErrorDiagnostic
                        diagnostic={diagnostic}
                        title="We could not load the events"
                    />
                )}

                {!loading && !diagnostic && (
                    <div className="events-grid">
                        {filteredEvents.map((event) => (
                            <EventCardV2
                                key={event.eventID}
                                eventId={event.eventID}
                                eventName={event.eventName}
                                eventLogo={event.eventLogoPath}
                                startTime={event.startTime}
                                endTime={event.endTime}
                                showDate={false}
                                onSignup={handleSelectEvent}
                                badgeText={config.badgeText}
                                isActive={event.active}
                                signupTextDa="Vælg Event"
                                signupTextEn="Choose Event"
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default EventListPage;